// Calls for the Google Drive knowledge sync (phase 3), through the typed
// hc<AppType> client: the admin "Dari Google Drive" section of Basis
// Pengetahuan (/admin/knowledge-sync/*) and the Library mirror
// (/knowledge/drive). Response types come from the routes. Errors become
// ApiError with the API's Indonesian message, like api.ts.
import type { InferResponseType } from "hono/client";
import { ApiError, errorMessage } from "./api";
import { API_URL, api } from "./client";

type SyncRoutes = (typeof api.admin)["knowledge-sync"];
type StatusRes = InferResponseType<SyncRoutes["status"]["$get"], 200>;
type FilesRes = InferResponseType<SyncRoutes["files"]["$get"], 200>;
type RunRes = InferResponseType<SyncRoutes["run"]["$post"], 200>;
type ReprocessRes = InferResponseType<SyncRoutes["files"][":driveId"]["reprocess"]["$post"], 200>;
type LibraryRes = InferResponseType<typeof api.knowledge.drive.$get, 200>;

export type SyncStatus = StatusRes;
export type SyncFilesPage = FilesRes;
export type SyncFile = FilesRes["files"][number];
export type SyncFileStatus = SyncFile["status"];
export type SyncSummary = RunRes["summary"];
export type SyncNotReadyReason = NonNullable<SyncStatus["reason"]>;
export type LibraryDriveFile = LibraryRes["files"][number];

export { errorMessage };

interface ResLike {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

async function send<T>(request: () => Promise<ResLike>): Promise<T> {
  let res: ResLike;
  try {
    res = await request();
  } catch {
    throw new ApiError(0, "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.");
  }
  if (res.ok) return (await res.json()) as T;
  let body: Record<string, unknown> | null = null;
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    // Not JSON.
  }
  const message = typeof body?.error === "string" && body.error.trim() ? body.error : "Terjadi kesalahan. Coba lagi.";
  throw new ApiError(res.status, message, null, null, body);
}

export async function getSyncStatus(): Promise<SyncStatus> {
  return send<StatusRes>(() => api.admin["knowledge-sync"].status.$get());
}

export async function listSyncFiles(input: { page?: number; pageSize?: number; status?: SyncFileStatus } = {}): Promise<SyncFilesPage> {
  const query = { page: String(input.page ?? 1), pageSize: String(input.pageSize ?? 25), ...(input.status ? { status: input.status } : {}) };
  return send<FilesRes>(() => api.admin["knowledge-sync"].files.$get({ query }));
}

/** Runs a sync now; 409 code "running" or "not_ready" (with the reason) as ApiError. */
export async function runSyncNow(): Promise<SyncSummary> {
  const body = await send<RunRes>(() => api.admin["knowledge-sync"].run.$post());
  return body.summary;
}

export async function reprocessSyncFile(driveId: string): Promise<SyncFile> {
  const body = await send<ReprocessRes>(() => api.admin["knowledge-sync"].files[":driveId"].reprocess.$post({ param: { driveId } }));
  return body.file;
}

export async function listLibraryDriveFiles(): Promise<LibraryDriveFile[]> {
  const body = await send<LibraryRes>(() => api.knowledge.drive.$get());
  return body.files;
}

/** Download URL of the original in Drive (a normal navigation; the session cookie goes with it). */
export function libraryDriveDownloadUrl(driveId: string): string {
  return `${API_URL}/knowledge/drive/${encodeURIComponent(driveId)}/download`;
}
